"use client";

/**
 * The one component pages embed for a full marketing video. Renders by
 * manifest availability:
 *   - published/preview → clickable poster + accessible modal player +
 *     collapsible transcript; a preview additionally carries a "preview"
 *     label and never a general-availability claim.
 *   - pending → an honest "the demo video appears here soon" note. Never an
 *     old cut, never a fake placeholder (mirrors VIDEO_PUBLISHING_CHECKLIST).
 *   - hidden → renders nothing.
 *
 * On non-Dutch pages a clear language indication is shown next to the poster
 * (the deliverables are Dutch-spoken with Dutch captions) — a Dutch video
 * never appears under an English page unlabelled.
 */

import { useEffect, useRef, useState } from "react";
import { Clapperboard } from "lucide-react";
import { useLocale, useT } from "@/i18n";
import { getMarketingVideo, isPlayable, type MarketingVideoId } from "@/lib/marketing-videos";
import { trackVideo } from "./video-analytics";
import { VideoModal } from "./video-modal";
import { VideoPoster } from "./video-poster";
import { VideoTranscript } from "./video-transcript";

export function VideoBlock({
  id,
  posterCaption,
  showTranscript = true,
  priority = false,
}: {
  id: MarketingVideoId;
  /** Short visible caption on the poster, e.g. "Bekijk FixControl in 60 seconden". */
  posterCaption?: string;
  showTranscript?: boolean;
  /** Poster is above the fold → prioritize its image load. */
  priority?: boolean;
}) {
  const t = useT();
  const { locale } = useLocale();
  const video = getMarketingVideo(id);
  const [open, setOpen] = useState(false);
  const openerRef = useRef<HTMLButtonElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // One impression per pageview, when the block actually scrolls into view.
  const impressionFired = useRef(false);
  useEffect(() => {
    const el = rootRef.current;
    if (!el || impressionFired.current || !isPlayable(video)) return;
    if (typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !impressionFired.current) {
          impressionFired.current = true;
          trackVideo(video.analyticsName, "impression");
          io.disconnect();
        }
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [video]);

  if (video.availability === "hidden") return null;

  if (!isPlayable(video)) {
    // Pending: honest implementation status, no player, no placeholder video.
    return (
      <div className="mx-auto flex max-w-3xl items-start gap-4 rounded-2xl border border-dashed border-line bg-bg-raised/60 p-6">
        <span className="mt-0.5 inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-line-subtle bg-bg-elev text-fg-muted">
          <Clapperboard size={18} />
        </span>
        <div>
          <div className="text-[14px] font-semibold text-fg-bright">
            {t("videoKit.pendingTitle")}
          </div>
          <p className="mt-1.5 text-[13px] leading-relaxed text-fg-dim">
            {t("videoKit.pendingBody")}
          </p>
        </div>
      </div>
    );
  }

  const ariaLabel = t("videoKit.playAriaLabel", {
    title: video.title,
    duration: `${Math.round(video.durationSeconds)}s`,
  });

  return (
    <div ref={rootRef} className="mx-auto max-w-4xl">
      <VideoPoster
        video={video}
        onOpen={() => setOpen(true)}
        ariaLabel={ariaLabel}
        caption={posterCaption}
        priority={priority}
        buttonRef={openerRef}
      />
      <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[12.5px] text-fg-muted">
        {video.availability === "preview" && <span>{t("videoKit.previewLabel")}</span>}
        {locale !== "nl" && <span>{t("videoKit.languageNote")}</span>}
        <span>{t("videoKit.captionsNote")}</span>
      </div>
      <VideoModal video={video} title={video.title} open={open} onClose={() => setOpen(false)} />
      {showTranscript && <VideoTranscript video={video} />}
    </div>
  );
}
